import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, DownloadSimple, FloppyDisk } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { StatementView } from "./ScheduleIIIStatements";

const STATEMENT_KEYS = ["udin", "balance-sheet", "profit-loss", "cash-flow",
  "sch-bs", "fa", "sch-pl", "notes-2", "soti", "d-tax", "fa-tax", "pro-bs", "pro-pl"];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Schedule III — full-screen financial statements workspace (same colour
// scheme as Ledger Views/Day Book). Sections will be developed one by one
// and stay attached to the software's live data.
const MENU = [
  { key: "data-input", label: "Data Input" },
  { key: "udin", label: "UDIN" },
  { key: "balance-sheet", label: "Balance Sheet" },
  { key: "profit-loss", label: "Profit and Loss Account" },
  { key: "cash-flow", label: "Cash Flow" },
  { key: "sch-bs", label: "Schedule Forming Part of Balance Sheet" },
  { key: "fa", label: "FA" },
  { key: "sch-pl", label: "Schedule Forming Parts of Statement of Profit and Loss Account" },
  { key: "notes-1", label: "Notes 1" },
  { key: "notes-2", label: "Notes 2" },
  { key: "notes-3", label: "Notes 3" },
  { key: "soti", label: "SOTI" },
  { key: "d-tax", label: "D Tax" },
  { key: "fa-tax", label: "FA Tax" },
  { key: "pro-bs", label: "Pro BS" },
  { key: "pro-pl", label: "Pro PL" },
  { key: "ratio", label: "Ratio" },
  { key: "reports", label: "Reports" },
  { key: "mlr", label: "MLR" },
  { key: "all-summary", label: "All Summary" },
];

function DataInputView({ onBack, company }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/schedule3/data-input`).then((r) => setRows(r.data.rows || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onBack(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onBack]);

  const setVal = (key, value) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, value } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      rows.forEach((r) => { if (!r.auto) body[r.key] = r.value || ""; });
      const res = await axios.put(`${API}/schedule3/data-input`, body);
      setRows(res.data.rows || []);
      toast.success("Data Input saved");
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const download = async (format) => {
    try {
      const res = await axios.get(`${API}/schedule3/data-input/export`, { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `data-input-summary.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="w-full" data-testid="s3-data-input-view">
      <div className="w-full overflow-hidden shadow-xl flex flex-col" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3", height: "calc(100vh - 0.5rem)" }}>
        <div className="flex items-center justify-between px-3 py-1.5 text-white" style={{ background: "#0f766e" }}>
          <span className="text-sm font-bold">Schedule III — Data Input</span>
          <span className="text-sm font-black uppercase tracking-wide">{company}</span>
          <button onClick={onBack} data-testid="s3-di-close" className="p-1 mr-12 hover:bg-white/20 rounded-sm">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="flex items-center justify-between px-4 py-1.5 border-b border-[#e7dcc3]">
          <div className="flex gap-2">
            <button onClick={() => download("xlsx")} data-testid="s3-di-download-excel"
              className="flex items-center gap-1.5 px-3 py-1 text-white text-xs font-bold rounded-sm hover:opacity-90" style={{ background: "#0f766e" }}>
              <DownloadSimple size={13} weight="bold" /> Download Excel
            </button>
            <button onClick={() => download("pdf")} data-testid="s3-di-download-pdf"
              className="flex items-center gap-1.5 px-3 py-1 text-white text-xs font-bold rounded-sm hover:opacity-90" style={{ background: "#0f766e" }}>
              <DownloadSimple size={13} weight="bold" /> Download PDF
            </button>
          </div>
          <button onClick={save} disabled={saving} data-testid="s3-di-save"
            className="flex items-center gap-1.5 px-4 py-1 text-white text-xs font-black rounded-sm hover:opacity-90 disabled:opacity-50" style={{ background: "#b45309" }}>
            <FloppyDisk size={13} weight="bold" /> {saving ? "Saving…" : "Save (Ctrl+Enter)"}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <form onSubmit={(e) => { e.preventDefault(); save(); }}>
            <div className="text-center text-base font-black text-slate-900 mb-3 tracking-wide">Data Input Summary</div>
            <div className="border-2 border-slate-700">
              <div className="flex text-sm font-bold text-slate-900 border-b-2 border-slate-700" style={{ background: "#f1e9d6" }}>
                <div className="w-[46px] px-2 py-1.5 text-center border-r border-slate-700">Sl</div>
                <div className="w-[280px] px-3 py-1.5 text-center border-r border-slate-700">Particular</div>
                <div className="flex-1 px-3 py-1.5 text-center">Details</div>
              </div>
              {rows.map((r) => (
                <div key={`${r.key}-${r.sl}`} className="flex text-sm text-slate-900 border-b border-slate-500 last:border-b-0">
                  <div className="w-[46px] px-2 py-1 text-center border-r border-slate-500 font-semibold">{r.sl}</div>
                  <div className="w-[280px] px-3 py-1 border-r border-slate-500 font-semibold">{r.particular}</div>
                  <div className="flex-1 px-2 py-0.5 flex items-center">
                    {r.auto ? (
                      <div className="w-full flex items-center justify-center gap-2">
                        <span className="text-center font-semibold" data-testid={`s3-di-auto-${r.key}`}>{r.value || "—"}</span>
                        <span className="text-[9px] font-black px-1 rounded-sm bg-teal-600 text-white uppercase" title="Auto-linked to software data">Auto</span>
                      </div>
                    ) : (
                      <input value={r.value || ""} onChange={(e) => setVal(r.key, e.target.value)}
                        data-testid={`s3-di-input-${r.key}`}
                        className="w-full bg-transparent text-center font-semibold outline-none focus:bg-white/70 rounded-sm px-2 py-0.5"
                        placeholder="Enter…" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-slate-500 font-semibold mt-2 text-center">
              Auto fields stay linked to your software — company name, address, financial year update automatically everywhere.
            </div>
            <button type="submit" className="hidden" />
          </form>
        </div>
        <div className="h-1.5" style={{ background: "#0f766e" }} />
      </div>
    </div>
  );
}

function SectionView({ item, onBack, company }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onBack(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onBack]);
  return (
    <div className="w-full" data-testid="s3-section-view">
      <div className="w-full overflow-hidden shadow-xl flex flex-col" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3", height: "calc(100vh - 0.5rem)" }}>
        <div className="flex items-center justify-between px-3 py-1.5 text-white" style={{ background: "#0f766e" }}>
          <span className="text-sm font-bold">Schedule III — {item.label}</span>
          <span className="text-sm font-black uppercase tracking-wide">{company}</span>
          <button onClick={onBack} data-testid="s3-section-close" className="p-1 mr-12 hover:bg-white/20 rounded-sm">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center px-8">
            <div className="text-lg font-black text-slate-800 tracking-wide" data-testid="s3-section-title">{item.label}</div>
            <div className="mt-3 text-sm text-slate-600 max-w-md">
              This section is being prepared. It will stay connected to your live software data (bills, ledgers, vouchers) automatically.
            </div>
            <div className="mt-5 text-[11px] font-semibold text-slate-500">Esc — Back to Schedule III</div>
          </div>
        </div>
        <div className="h-1.5" style={{ background: "#0f766e" }} />
      </div>
    </div>
  );
}

export default function ScheduleIII() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const company = user?.company_name || "";
  const [selected, setSelected] = useState(0);
  const [openTab, setOpenTab] = useState(null);

  const activate = useCallback((idx) => {
    setOpenTab(MENU[idx]);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (openTab) return; // SectionView handles its own keys
      if (e.key === "ArrowDown") { e.preventDefault(); setSelected((s) => (s + 1) % MENU.length); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSelected((s) => (s - 1 + MENU.length) % MENU.length); }
      else if (e.key === "Enter") { e.preventDefault(); activate(selected); }
      else if (e.key === "Escape") { e.preventDefault(); navigate("/dashboard"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, openTab, activate, navigate]);

  if (openTab) {
    if (openTab.key === "data-input") {
      return <DataInputView onBack={() => setOpenTab(null)} company={company} />;
    }
    if (STATEMENT_KEYS.includes(openTab.key)) {
      return <StatementView stmt={openTab.key} label={openTab.label} onBack={() => setOpenTab(null)} company={company} />;
    }
    return <SectionView item={openTab} onBack={() => setOpenTab(null)} company={company} />;
  }

  return (
    <div className="w-full" data-testid="schedule-iii-page">
      <div className="w-full overflow-hidden shadow-xl flex flex-col" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3", height: "calc(100vh - 0.5rem)" }}>
        <div className="flex items-center justify-between px-3 py-1.5 text-white" style={{ background: "#0f766e" }} data-testid="schedule-iii-header">
          <span className="text-sm font-bold">Schedule III</span>
          <span className="text-sm font-black uppercase tracking-wide">{company}</span>
          <button onClick={() => navigate("/dashboard")} data-testid="schedule-iii-close" className="p-1 mr-12 hover:bg-white/20 rounded-sm">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="flex items-end border-b-2 border-slate-700 text-xs font-bold text-slate-800">
          <div className="flex-1 px-4 py-1 tracking-[0.25em]">Particulars</div>
          <div className="w-[120px] px-2 py-1 text-right">Section</div>
        </div>
        <div className="flex-1 overflow-y-auto py-1 select-none" data-testid="schedule-iii-menu">
          {MENU.map((item, idx) => (
            <div
              key={item.key}
              data-testid={`s3-menu-${item.key}`}
              onClick={() => { setSelected(idx); activate(idx); }}
              onMouseEnter={() => setSelected(idx)}
              ref={selected === idx ? (el) => el && el.scrollIntoView({ block: "nearest" }) : undefined}
              className={`flex items-center cursor-pointer text-[15px] ${
                selected === idx ? "bg-teal-600 text-white shadow-sm" : "text-slate-800 hover:bg-[#f1e9d6]"
              }`}
            >
              <div className="flex-1 px-4 py-1 font-semibold">{item.label}</div>
              <div className={`w-[120px] px-3 py-1 text-right text-xs font-bold ${selected === idx ? "text-teal-100" : "text-slate-400"}`}>{idx + 1}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t-2 border-slate-700 bg-[#f1e9d6] px-4 py-1.5 text-[11px] font-bold text-slate-700">
          <span>↑↓ Navigate · Enter Open · Esc Back</span>
          <span>{MENU.length} Sections</span>
        </div>
      </div>
    </div>
  );
}
